import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "components/Table/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import productData from "./data.json";
import { Spinner, SwitchV2 } from "components";
import { useForm } from "react-hook-form";
import { AxiosRequestConfig } from "axios";
import {
  addEditProducts,
  AddEditProductsRequestType,
} from "service/apis/PartnerDetails";
import { useParams } from "react-router-dom";
import {
  getPartnerDefaultCommission,
  GetPartnerDefaultCommissionRequestType,
  getProductV3,
  GetProductV3RequestType,
} from "service/apis/PartnerRegistrationapi";
import { toast } from "sonner";

type ApiPurpose = {
  id: number;
  created_at: number;
  channel_id: number;
  purpose_id: number;
  charges: number;
  _purpose_mapper: {
    id: number;
    created_at: number;
    name: string;
    description: string | null;
    tcs_type: string;
    default_default_commission: number;
    default_walkin: number;
    loan: boolean;
    product_id: number;
    mode: string;
  };
};

type ApiResponse = ApiPurpose[];

function mapApiToFormData(apiData: ApiResponse): typeof productData {
  if (!apiData) {
    return productData;
  }
  apiData = apiData.filter((item) => item.purpose_id !== 0);
  return {
    product_category: [
      {
        id: 1,
        name: "Remittance",
      },
      {
        id: 2,
        name: "Forex",
      },
      {
        id: 3,
        name: "Visa",
      },
      {
        id: 4,
        name: "Travel",
      },
      {
        id: 5,
        name: "Study Abroad",
      },
    ],
    products: {
      Remittance: apiData?.map((item) => ({
        id: item.purpose_id,
        product: item?._purpose_mapper?.name,
        sub_category: "",
        currency: "INR",
        margin: item.charges.toString(),
        new_margin: item.charges.toString(),
        status: "default",
        active: true,
        is_modified: false,
      })),
      // Add empty arrays for other categories or map accordingly
      Forex: [],
      Visa: [],
      Travel: [],
      "Study Abroad": [],
    },
  };
}

type ProductType = {
  id: number;
  product: string;
  sub_category: string;
  currency: string;
  margin: string;
  new_margin: string;
  status: string;
  active: boolean;
  county_wise_margin?: {
    country: string;
    sell_margin: string;
    buy_margin: string;
  }[];
  is_modified: boolean;
  // Forex specific fields
  sell_margin?: string;
  buy_margin?: string;
};

type FormValues = {
  products: {
    [key: number]: {
      id: string | number;
      active: boolean;
      currency: string;
      margin: string;
      new_margin: string;
      is_modified: boolean;
      status: string;
      // Forex specific
      sell_margin?: string;
      buy_margin?: string;
      county_wise_margin?: {
        country: string;
        sell_margin: string;
        buy_margin: string;
      }[];
    };
  };
};

const Product = ({ dialogOpen, setDialogOpen }: any) => {
  const [activeTab, setActiveTab] = useState("Remittance");
  const [showCountryMargins, setShowCountryMargins] = useState<number | null>(
    null,
  );
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [forexResponse, setForexResponse] = useState<any>([]);
  const [standardMargin, setStandardMargin] = useState<
    {
      buy: string;
      sell: string;
    }[]
  >([]);
  const [tablePreviewMode, setTablePreviewMode] = useState({
    isOpen: false,
    data: null,
  });

  const { id } = useParams();

  async function fetchCommisionRates() {
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const req: AxiosRequestConfig<GetPartnerDefaultCommissionRequestType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        partner_id: id,
        producttype_id: null,
      },
    };
    try {
      const res = await getPartnerDefaultCommission(req);
      return res.data?.listOfComm;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async function fetchProducts() {
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const req: AxiosRequestConfig<GetProductV3RequestType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      params: {
        product: "Forex",
        partner_id: id,
      },
    };
    try {
      const res = await getProductV3(req);
      return res.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        // Replace with your actual API call
        const response = (await fetchCommisionRates()) as ApiResponse;
        const response2 = (await fetchProducts()) as ApiResponse;
        if (response2?.length > 0) {
          setForexResponse(
            response2?.map((item: any) => {
              return {
                id: item.id,
                name: item.product_type_name,
                product_id: item.product_id,
                isActive: item?.productActive,
                toggleActive: item?.productActive ? true : false,
                commission_charges: item?.commission_charges?.map(
                  (charge: any) => {
                    return {
                      ...charge,
                      original_charges: charge.charges,
                    };
                  },
                ),
                original_commission_charges: item?.commission_charges,
                isStandardMarginSet: checkBuySellMarginSame(
                  item?.commission_charges,
                ),
                isModified: false,
              };
            }),
          );
        }
        setApiData(response);
      } catch (err) {
        setError("Failed to load product data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const formData = mapApiToFormData(apiData as ApiResponse);

  const { watch, setValue, reset, handleSubmit } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      products: Object.fromEntries(
        Object.values(productData.products)
          .flat()
          .map((product) => [
            product?.id,
            {
              id: product?.id,
              active: product?.active,
              currency: product?.currency,
              margin: product?.margin,
              name: product?.product,
              new_margin: product?.new_margin,
              status: product.status,
              is_modified: product?.is_modified,
              sell_margin: product?.sell_margin,
              buy_margin: product?.buy_margin,
              county_wise_margin: product?.county_wise_margin,
            },
          ]),
      ),
    },
  });

  useEffect(() => {
    if (apiData) {
      reset({
        products: Object.fromEntries(
          Object.values(formData.products)
            .flat()
            .map((product) => [
              product?.id,
              {
                id: product?.id,
                active: product?.active, // This will now correctly set `active: true`
                currency: product?.currency,
                margin: product?.margin,
                name: product?.product,
                new_margin: product?.new_margin,
                status: product.status,
                is_modified: product?.is_modified,
                sell_margin: product?.sell_margin,
                buy_margin: product?.buy_margin,
                county_wise_margin: product?.county_wise_margin,
              },
            ]),
        ),
      });
    }
  }, [apiData, reset]);

  // Watch all form values to trigger re-renders on changes
  const formValues = watch();

  const onSubmit = async (data: FormValues) => {
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    setLoading(true);
    const activateForexProducts = forexResponse.filter(
      (item: any) => item.toggleActive,
    );
    const req: AxiosRequestConfig<AddEditProductsRequestType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        child_channel: id,
        approved_commision_rates_remit: Object.values(data.products).map(
          (product) => ({
            purpose_id: product.id,
            charges: product.new_margin,
            product_id: 0,
          }),
        ),
        approved_comision_rates_forex: activateForexProducts.flatMap(
          (product: any) => {
            // commission_charges is an array of objects
            return product.commission_charges.map((charge: any) => ({
              producttype_id: charge.producttype_id,
              rate: charge.charges,
              ProductSubtype: charge.ProductSubtype,
              currency: charge.Country_Currency_ISO,
            }));
          },
        ),
      },
    };

    try {
      const res = (await addEditProducts(req)) as any;
      if (res?.data) {
        toast.success("Product added/modified successfully");
      }
      setLoading(false);
      setDialogOpen(false);
      setTablePreviewMode({
        isOpen: false,
        data: null,
      });
      reset();
      setForexResponse([]);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to add/modify product");
    }

    console.log("Form submitted:", data);
    // Here you would typically send the data to your API
  };

  const handleToggleProduct = (productId: number, value: boolean) => {
    setValue(`products.${productId}.active` as any, value);
    if (!value) {
      // Reset to original values when deactivating
      const originalProduct = Object.values(productData.products)
        .flat()
        .find((p) => p.id === productId);
      if (originalProduct) {
        setValue(
          `products.${productId}.new_margin` as any,
          originalProduct.margin,
        );
        setValue(
          `products.${productId}.currency` as any,
          originalProduct.currency,
        );
        if (originalProduct.sell_margin) {
          setValue(
            `products.${productId}.sell_margin` as any,
            originalProduct.sell_margin,
          );
        }
        if (originalProduct.buy_margin) {
          setValue(
            `products.${productId}.buy_margin` as any,
            originalProduct.buy_margin,
          );
        }
      }
    }
    setValue(`products.${productId}.is_modified` as any, true);
  };

  const handleForexToggleProduct = (productId: number, value: boolean) => {
    setForexResponse((prevState: any) =>
      prevState.map((item: any) =>
        item.id === productId ? { ...item, toggleActive: value } : item,
      ),
    );
  };

  const handleMarginChange = (
    productId: number,
    field: string,
    value: string,
  ) => {
    const product = formValues.products[productId];
    const originalMargin = product?.margin;

    // Case 1: Empty string - revert to original value
    if (value === "") {
      setValue(`products.${productId}.${field}` as any, originalMargin);
      setValue(`products.${productId}.is_modified` as any, false);
      return;
    }

    // Case 2: Single "0" - keep as "0" but mark as modified if different from original
    if (value === "0") {
      setValue(`products.${productId}.${field}` as any, "0");
      setValue(
        `products.${productId}.is_modified` as any,
        Number(originalMargin) !== 0,
      );
      return;
    }

    // Case 3: Leading zeros (except decimal numbers)
    if (/^0+[1-9]/.test(value)) {
      // Matches "012" but not "0.12"
      const sanitizedValue = value.replace(/^0+/, "");
      setValue(`products.${productId}.${field}` as any, sanitizedValue);
      setValue(
        `products.${productId}.is_modified` as any,
        Number(originalMargin) !== Number(sanitizedValue),
      );
      return;
    }

    // Case 4: Decimal numbers starting with 0 (like 0.4) - keep as-is
    if (/^0\.\d+$/.test(value)) {
      setValue(`products.${productId}.${field}` as any, value);
      setValue(
        `products.${productId}.is_modified` as any,
        Number(originalMargin) !== Number(value),
      );
      return;
    }

    // Default case: Any other valid number
    setValue(`products.${productId}.${field}` as any, value);
    setValue(
      `products.${productId}.is_modified` as any,
      Number(originalMargin) !== Number(value),
    );
  };

  const hasModifications = useMemo(() => {
    // Check Remittance products
    const remittanceModified = Object.values(formValues.products || {}).some(
      (product: any) => product.is_modified,
    );

    // Check Forex products
    const forexModified = forexResponse?.some(
      (product: any) => product.isModified,
    );

    return remittanceModified || forexModified;
  }, [formValues, forexResponse]);

  // Columns for non-Forex products
  const columns = useMemo<ColumnDef<ProductType>[]>(
    () => [
      {
        accessorKey: "product",
        header: "Product",
        cell: ({ row }) => {
          const product = row.original;
          const isActive = formValues.products[product.id]?.active || false;

          return (
            <div className="flex items-center gap-2">
              <SwitchV2
                setIsToggled={(val: boolean) =>
                  handleToggleProduct(product.id, val)
                }
                isToggled={isActive}
                disabled={isActive}
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{product.product}</span>
                <span className="text-xs text-gray-500">
                  {product.sub_category}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "new_margin",
        header: "Margin",
        cell: ({ row }) => {
          const product = row.original;
          const isActive = formValues.products[product.id]?.active || false;
          const isModified =
            formValues.products[product.id]?.is_modified || false;
          const currency =
            formValues.products[product.id]?.currency || product.currency;
          const margin =
            formValues.products[product.id]?.new_margin || product.new_margin;
          return (
            <div className="flex flex-col items-center justify-between gap-2 w-full">
              <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden  p-1 h-11">
                <Select
                  // onValueChange={(val) => handleMarginChange(product.id, 'currency', val)}
                  disabled
                >
                  <SelectTrigger className="w-[70px] disabled:cursor-default h-9 text-sm border-none text-blue-700 bg-[#E1EBFC] rounded-md focus:ring-0">
                    <SelectValue placeholder="INR" />
                  </SelectTrigger>
                  <SelectContent className="">
                    <SelectItem className="text-blue-700" value="commission">
                      INR
                    </SelectItem>
                    <SelectItem className="text-blue-700" value="flat">
                      Flat
                    </SelectItem>
                  </SelectContent>
                </Select>
                <input
                  value={margin}
                  type="number"
                  key={`margin-${product.id}`}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                  }}
                  onChange={(e) => {
                    handleMarginChange(
                      product.id,
                      "new_margin",
                      e.target.value,
                    );
                  }}
                  disabled={!isActive}
                  className="w-full h-9 text-sm border-none  rounded-none disabled:text-gray-400"
                />
                {isModified ? (
                  <span className="text-xs text-gray-400 pr-2">Proposed</span>
                ) : (
                  <span className="text-xs text-gray-400 pr-2">Default</span>
                )}
              </div>
              {isModified && (
                <div className="flex w-full justify-between">
                  <small className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                    INR {product.margin}
                  </small>
                  <small className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                    Previous value
                  </small>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [formValues.products],
  );

  const handleDataOverview = () => {
    // Extract non-Forex products from formValues
    const nonForexProducts = Object.values(formValues.products);

    // Extract Forex products from forexResponse
    const forexProducts = forexResponse.map((product: any) => ({
      id: product.id,
      name: product.name,
      is_modified: product.isModified, // Use the updated isModified field
      status: product?.isActive
        ? "default"
        : product.toggleActive
          ? "active"
          : "default",
      new_margin: product.commission_charges
        .map((charge: any) => charge.charges)
        .join(", "),
      margin: product.commission_charges
        .map((charge: any) => charge.original_charges)
        .join(", "),
    }));

    // Combine both non-Forex and Forex products
    const allProducts = [...nonForexProducts, ...forexProducts];

    // Filter newly added products
    const newlyAddedProducts = allProducts.filter(
      (product) => product.status === "active" && product.is_modified,
    );

    // Filter modified products (exclude newly added products)
    const modifiedProducts = allProducts.filter(
      (product) => product.is_modified && product.status !== "active", // Exclude newly added products
    );

    // Update the form values
    setValue("newlyAddedProducts", newlyAddedProducts);
    setValue("modifiedProducts", modifiedProducts);
  };

  // const handleStandardMarginChange = (productId: number, value: string, ProductSubtype: 'Buy' | 'Sell') => {
  //   const product = forexResponse.find((item: any) => item.id === productId);
  //   const updatedCommissionCharges = product.commission_charges.map((charge: any) => {
  //     if (charge.ProductSubtype === ProductSubtype) {
  //       return { ...charge, charges: value || charge.original_charges };
  //     }
  //     return charge;
  //   });

  //   setForexResponse((prevState: any) =>
  //     prevState.map((item: any) => {
  //       if (item.id === productId) {
  //         return {
  //           ...item,
  //           commission_charges: updatedCommissionCharges,
  //           isStandardMarginSet: checkBuySellMarginSame(updatedCommissionCharges), // Revalidate here
  //         };
  //       }
  //       return item;
  //     })
  //   );
  //   updateForexModifiedStatus();
  // };

  const handleCurrencyMargin = (
    productId: number,
    commissionId: number,
    value: string,
  ) => {
    setForexResponse((prevState: any) =>
      prevState.map((item: any) => {
        if (item.id === productId) {
          // maintain a modified tag of changed charges
          const originalCharges = item.original_commission_charges.find(
            (charge: any) => charge.id === commissionId,
          )?.charges;
          const isModified = Number(originalCharges) !== Number(value);
          const updatedCommissionCharges = item.commission_charges.map(
            (charge: any) =>
              charge.id === commissionId
                ? {
                    ...charge,
                    charges: value || charge.original_charges,
                    isModified,
                  }
                : charge,
          );
          return {
            ...item,
            commission_charges: updatedCommissionCharges,
            isStandardMarginSet: checkBuySellMarginSame(
              updatedCommissionCharges,
            ), // Revalidate here
          };
        }
        return item;
      }),
    );

    // Revalidate the modified status
    updateForexModifiedStatus();
  };

  const handleCommissionTypeChange = (
    productId: number,
    commissionId: number,
    value: string,
  ) => {
    setForexResponse((prevState: any) =>
      prevState.map((item: any) => {
        if (item.id === productId) {
          const updatedCommissionCharges = item.commission_charges.map(
            (charge: any) =>
              charge.id === commissionId
                ? { ...charge, CommisionType: value }
                : charge,
          );

          return {
            ...item,
            commission_charges: updatedCommissionCharges,
            isStandardMarginSet: checkBuySellMarginSame(
              updatedCommissionCharges,
            ), // Revalidate here
          };
        }
        return item;
      }),
    );
    updateForexModifiedStatus();
  };

  const updateForexModifiedStatus = () => {
    setForexResponse((prevState: any) =>
      prevState.map((product: any) => ({
        ...product,
        isModified: isForexModified(product),
      })),
    );
  };

  if (loading) return <Spinner />;

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogPortal>
        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="bg-white-A700 flex flex-col rounded-lg p-0 h-[80vh] sm:h-[90vh] max-w-2xl animate-contentShow">
          {/* Dialog Header */}
          <AlertDialogHeader className="text-left border-b rounded-t h-min py-5 px-10 gap-0">
            <AlertDialogTitle className="font-bold p-0 text-lg text-black-label">
              {reviewMode ? "Review changes" : "Add product(s)"}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="h-full overflow-auto">
            {tablePreviewMode?.isOpen ? (
              <div className="px-2">
                <CurrencyMarginTable
                  showModified={true}
                  item={tablePreviewMode?.data}
                  handleCurrencyMargin={handleCurrencyMargin}
                  forexResponse={forexResponse}
                  handleCommissionTypeChange={handleCommissionTypeChange}
                />
              </div>
            ) : (
              <div className="bg-[#ffffff] h-full overflow-auto px-[10px] text-gray-400 rounded-lg">
                {/* You can add your form or other content here later */}
                <div className="w-full">
                  {!reviewMode ? (
                    <form className="" onSubmit={(e) => e.preventDefault()}>
                      <Tabs
                        defaultValue="Remittance"
                        value={activeTab}
                        onValueChange={setActiveTab}
                      >
                        <TabsList className="w-full hide-scrollbar flex rounded-none gap-2 h-9">
                          {productData?.product_category.map((tab) => (
                            <TabsTrigger
                              key={tab.id}
                              disabled={[3, 2, 4, 5].includes(tab.id)}
                              value={tab.name}
                              className={`px-6 py-3.5 text-sm h-9 w-full ${
                                tab.name === activeTab
                                  ? "border border-blue-700 text-blue-700 font-semibold shadow bg-[#EFF4FF] rounded-md"
                                  : "bg-[#F5F5F5] text-black font-medium rounded-md"
                              }`}
                            >
                              <div className="flex">{tab.name}</div>
                            </TabsTrigger>
                          ))}
                        </TabsList>

                        {productData?.product_category.map((tab) => {
                          if (tab.name === "Forex") {
                            return (
                              <TabsContent value={tab.name} key={tab.id}>
                                <div className="space-y-6 py-2">
                                  {forexResponse?.map((item: any) => {
                                    return (
                                      <div
                                        key={item.id}
                                        className=" rounded-md border p-3 space-y-4"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <SwitchV2
                                              disabled={item.isActive}
                                              setIsToggled={(val: boolean) =>
                                                handleForexToggleProduct(
                                                  item.id,
                                                  val,
                                                )
                                              }
                                              isToggled={item.toggleActive}
                                            />
                                            <span className="font-medium text-gray-800">
                                              {item.name}
                                            </span>
                                          </div>
                                          <button
                                            type="button"
                                            disabled={!item.toggleActive}
                                            className="text-blue-600 text-sm w-1/2 disabled:opacity-40 disabled:cursor-not-allowed"
                                            onClick={() =>
                                              setShowCountryMargins(
                                                showCountryMargins === item.id
                                                  ? null
                                                  : item.id,
                                              )
                                            }
                                          >
                                            {item.isStandardMarginSet ? (
                                              <div className="bg-[#E1EBFC] p-3 flex items-center w-full justify-between rounded-md">
                                                <p className="text-blue-700">
                                                  Set Custom Margins
                                                </p>
                                                <svg
                                                  width="16"
                                                  height="17"
                                                  viewBox="0 0 16 17"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M3.33594 4.49609H2.5026C2.06058 4.49609 1.63665 4.67169 1.32409 4.98425C1.01153 5.29681 0.835938 5.72073 0.835938 6.16276V13.6628C0.835938 14.1048 1.01153 14.5287 1.32409 14.8413C1.63665 15.1538 2.06058 15.3294 2.5026 15.3294H10.0026C10.4446 15.3294 10.8686 15.1538 11.1811 14.8413C11.4937 14.5287 11.6693 14.1048 11.6693 13.6628V12.8294"
                                                    stroke="#165FE3"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                  />
                                                  <path
                                                    d="M10.8333 2.83472L13.3333 5.33472M14.4875 4.15556C14.8157 3.82735 15.0001 3.38221 15.0001 2.91806C15.0001 2.45391 14.8157 2.00876 14.4875 1.68056C14.1593 1.35235 13.7142 1.16797 13.25 1.16797C12.7858 1.16797 12.3407 1.35235 12.0125 1.68056L5 8.66806V11.1681H7.5L14.4875 4.15556Z"
                                                    stroke="#165FE3"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                  />
                                                </svg>
                                              </div>
                                            ) : (
                                              <div className="bg-[#E1EBFC] p-3 flex items-center w-full justify-between rounded-md">
                                                <div className="relative flex items-center gap-2">
                                                  <p className="text-blue-700">
                                                    Custom margins are set
                                                  </p>
                                                  {item.isModified && (
                                                    <p className="absolute -right-2 -top-1">
                                                      <svg
                                                        width="8"
                                                        height="8"
                                                        viewBox="0 0 8 8"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                      >
                                                        <circle
                                                          cx="4"
                                                          cy="4"
                                                          r="4"
                                                          fill="#165FE3"
                                                        />
                                                      </svg>
                                                    </p>
                                                  )}
                                                </div>
                                                <svg
                                                  width="16"
                                                  height="17"
                                                  viewBox="0 0 16 17"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M3.33594 4.49609H2.5026C2.06058 4.49609 1.63665 4.67169 1.32409 4.98425C1.01153 5.29681 0.835938 5.72073 0.835938 6.16276V13.6628C0.835938 14.1048 1.01153 14.5287 1.32409 14.8413C1.63665 15.1538 2.06058 15.3294 2.5026 15.3294H10.0026C10.4446 15.3294 10.8686 15.1538 11.1811 14.8413C11.4937 14.5287 11.6693 14.1048 11.6693 13.6628V12.8294"
                                                    stroke="#165FE3"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                  />
                                                  <path
                                                    d="M10.8333 2.83472L13.3333 5.33472M14.4875 4.15556C14.8157 3.82735 15.0001 3.38221 15.0001 2.91806C15.0001 2.45391 14.8157 2.00876 14.4875 1.68056C14.1593 1.35235 13.7142 1.16797 13.25 1.16797C12.7858 1.16797 12.3407 1.35235 12.0125 1.68056L5 8.66806V11.1681H7.5L14.4875 4.15556Z"
                                                    stroke="#165FE3"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                  />
                                                </svg>
                                              </div>
                                            )}
                                          </button>
                                        </div>

                                        {/* Middle Section */}

                                        {showCountryMargins === item.id &&
                                          item.toggleActive && (
                                            <>
                                              {/* {
                                                item.toggleActive &&
                                                <div className="space-y-2 w-full">
                                                  <p className="text-sm text-gray-500">Standard margin for all currencies:</p>
                                                  <div className="flex items-center gap-10 w-full">
                                                    <div className="flex flex-col space-y-1 w-full">
                                                      <span className="text-sm font-medium text-gray-700">Buying</span>
                                                      <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden  p-1 h-11">
                                                        <Select
                                                          defaultValue="commision"
                                                        >
                                                          <SelectTrigger disabled className="w-[70px] h-9 text-sm border-none text-blue-700 bg-[#E1EBFC] rounded-md focus:ring-0">
                                                            <SelectValue placeholder="%" />
                                                          </SelectTrigger>
                                                          <SelectContent>
                                                            <SelectItem className="text-blue-700" value="commision">%</SelectItem>
                                                            <SelectItem className="text-blue-700" value="Flat">Flat</SelectItem>
                                                          </SelectContent>
                                                        </Select>
                                                        <Input
                                                          type="number"
                                                          // value={productData.buy_margin || item.buy_margin}
                                                          onChange={(e) => handleStandardMarginChange(item.id, e.target.value, 'Buy')}
                                                          className="w-full h-9 text-sm border-none rounded-none disabled:text-gray-400"
                                                        />
                                                      </div>
                                                      <span className="text-xs text-gray-400">Default</span>
                                                    </div>
    
                                                    <div className="flex flex-col space-y-1 w-full">
                                                      <span className="text-sm font-medium text-gray-700">Selling</span>
                                                      <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden  p-1 h-11">
                                                        <Select
                                                          defaultValue="commision"
                                                        >
                                                          <SelectTrigger disabled className="w-[70px] h-9 text-sm border-none text-blue-700 bg-[#E1EBFC] rounded-md focus:ring-0">
                                                            <SelectValue placeholder="%" />
                                                          </SelectTrigger>
                                                          <SelectContent>
                                                            <SelectItem className="text-blue-700" value="commision">%</SelectItem>
                                                            <SelectItem className="text-blue-700" value="Flat">Flat</SelectItem>
                                                          </SelectContent>
                                                        </Select>
                                                        <Input
                                                          type="number"
                                                          // value={productData.sell_margin || item.sell_margin}
                                                          onChange={(e) => handleStandardMarginChange(item.id, e.target.value, 'Sell')}
                                                          autoFocus
                                                          className="w-full h-9 text-sm border-none rounded-none disabled:text-gray-400"
                                                        />
                                                      </div>
                                                      <span className="text-xs text-gray-400">Default</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              } */}
                                              <CurrencyMarginTable
                                                item={item}
                                                handleCurrencyMargin={
                                                  handleCurrencyMargin
                                                }
                                                forexResponse={forexResponse}
                                                handleCommissionTypeChange={
                                                  handleCommissionTypeChange
                                                }
                                              />
                                            </>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </TabsContent>
                            );
                          }
                          return (
                            <TabsContent key={tab.id} value={tab.name}>
                              <div className="pt-4">
                                <DataTable
                                  hoverRow={true}
                                  columns={columns}
                                  pageSize={100}
                                  highlightRow={false}
                                  data={
                                    formData?.products[
                                      tab.name as keyof typeof formData.products
                                    ]
                                  }
                                  isToolbarVisible={false}
                                  isPaginationVisible={
                                    formData?.products[
                                      tab.name as keyof typeof formData.products
                                    ]?.length > 10
                                  }
                                />
                              </div>
                            </TabsContent>
                          );
                        })}
                      </Tabs>
                    </form>
                  ) : (
                    <div className="flex-1 h-full">
                      <div className="max-w-5xl mx-auto">
                        {/* Products Added Section */}
                        {!formValues?.newlyAddedProducts?.length &&
                          !formValues?.modifiedProducts?.length && (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-gray-500">
                                No products added or modified.
                              </p>
                            </div>
                          )}
                        {formValues?.newlyAddedProducts?.length > 0 && (
                          <div className="">
                            <h2 className="text-blue-700 text-lg font-semibold mb-2 leading-[100%] flex items-center gap-3">
                              <svg
                                width="34"
                                height="16"
                                viewBox="0 0 34 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M26.0569 16.014H3.4425C2.63516 16.014 1.8609 15.6893 1.29002 15.1114C0.71915 14.5334 0.398438 13.7496 0.398438 12.9323V3.07387C0.398438 2.25656 0.71915 1.47272 1.29002 0.894791C1.8609 0.316864 2.63516 -0.0078125 3.4425 -0.0078125H26.0569C26.4768 -0.00780807 26.8922 0.0801388 27.2769 0.250499C27.6616 0.420858 28.0073 0.669947 28.2923 0.982104L32.7929 5.91131C33.3128 6.48075 33.6016 7.22766 33.6016 8.00308C33.6016 8.77849 33.3128 9.52541 32.7929 10.0948L28.2923 15.0241C28.0073 15.3362 27.6616 15.5853 27.2769 15.7557C26.8922 15.926 26.4768 16.014 26.0569 16.014Z"
                                  fill="#E1EBFC"
                                />
                                <path
                                  d="M7.67253 5.69518L9.91971 9.13767V5.66331C9.91971 5.43752 9.96764 5.26811 10.0635 5.15508C10.1113 5.09909 10.171 5.05478 10.2382 5.02547C10.3053 4.99615 10.3781 4.98259 10.4512 4.9858C10.5257 4.98222 10.6 4.99557 10.6688 5.02486C10.7376 5.05415 10.799 5.09864 10.8485 5.15508C10.9457 5.26815 10.9944 5.43756 10.9946 5.66331V10.2572C10.9946 10.7699 10.7847 11.0262 10.3649 11.0261C10.2686 11.0275 10.1727 11.0121 10.0815 10.9804C9.99425 10.9479 9.91405 10.8984 9.8454 10.8349C9.76878 10.7652 9.70013 10.687 9.64074 10.6019C9.5777 10.5129 9.51472 10.4218 9.45181 10.3288L7.25901 6.9263V10.349C7.25901 10.5721 7.20793 10.7408 7.10575 10.855C7.05638 10.9108 6.99559 10.955 6.92761 10.9846C6.85963 11.0141 6.78611 11.0283 6.71216 11.0261C6.63731 11.0291 6.56275 11.0151 6.49393 10.9851C6.42512 10.9552 6.3638 10.91 6.31445 10.853C6.21467 10.7375 6.16486 10.5694 6.16504 10.3488V5.84254C6.15909 5.68979 6.18041 5.5372 6.22799 5.39212C6.27896 5.27099 6.36556 5.16871 6.47602 5.09921C6.58678 5.02484 6.71678 4.98529 6.84969 4.98553C6.93638 4.98206 7.02281 4.99732 7.10323 5.0303C7.18364 5.06327 7.25619 5.11321 7.31606 5.17679C7.3812 5.24653 7.43922 5.32275 7.48924 5.40429C7.54835 5.49733 7.60944 5.59429 7.67253 5.69518ZM15.9542 5.99797H13.3213V7.43239H15.7457C15.9242 7.43239 16.0574 7.47288 16.1452 7.55387C16.1889 7.59438 16.2233 7.64405 16.2461 7.69941C16.2689 7.75476 16.2795 7.81448 16.2771 7.8744C16.2793 7.93474 16.2688 7.99485 16.2464 8.05082C16.2241 8.10678 16.1903 8.15733 16.1473 8.19918C16.0606 8.28308 15.9267 8.32498 15.7457 8.32489H13.3213V9.98627H16.0448C16.2284 9.98627 16.3668 10.0295 16.4601 10.1159C16.5065 10.1597 16.543 10.2132 16.5671 10.2727C16.5912 10.3322 16.6022 10.3963 16.5995 10.4605C16.6017 10.5235 16.5903 10.5862 16.5663 10.6443C16.5422 10.7024 16.506 10.7545 16.4601 10.797C16.3668 10.8836 16.2284 10.9268 16.0448 10.9266H12.8686C12.614 10.9266 12.431 10.8695 12.3196 10.7552C12.2082 10.641 12.1525 10.4563 12.1524 10.2013V5.81067C12.1472 5.66789 12.1728 5.52567 12.2273 5.39387C12.2742 5.28919 12.3575 5.20561 12.4613 5.15904C12.5904 5.10537 12.7292 5.08016 12.8686 5.08509H15.9542C16.1405 5.08509 16.2789 5.12689 16.3695 5.21047C16.4143 5.25201 16.4497 5.30292 16.4731 5.35968C16.4965 5.41643 16.5075 5.47767 16.5052 5.53914C16.5078 5.60124 16.4969 5.66315 16.4735 5.72058C16.4501 5.77802 16.4146 5.82961 16.3695 5.87178C16.2789 5.95604 16.1405 5.9981 15.9542 5.99797ZM21.4931 10.0061L20.5601 6.50358L19.6155 10.0061C19.5421 10.272 19.4838 10.4626 19.4406 10.5781C19.3928 10.6996 19.3148 10.8066 19.2142 10.8887C19.0922 10.9852 18.9399 11.034 18.7853 11.0261C18.6616 11.0311 18.5387 11.003 18.4291 10.9447C18.3349 10.8891 18.2568 10.8094 18.2028 10.7134C18.1401 10.6022 18.0925 10.483 18.0611 10.3589C18.0242 10.2221 17.9914 10.0952 17.9625 9.9782L17.0022 6.04583C16.9537 5.87576 16.9248 5.7006 16.9159 5.52382C16.9145 5.4525 16.9277 5.38166 16.9548 5.31581C16.982 5.24997 17.0223 5.19056 17.0733 5.14136C17.1246 5.09025 17.1856 5.05013 17.2525 5.02341C17.3195 4.9967 17.3911 4.98393 17.463 4.98587C17.6755 4.98587 17.8185 5.05493 17.8919 5.19306C17.9827 5.38389 18.0475 5.58629 18.0845 5.79474L18.8402 9.20543L19.6864 6.01397C19.7324 5.82487 19.7889 5.63858 19.8557 5.45598C19.9071 5.32607 19.9912 5.21202 20.0996 5.12516C20.2324 5.02583 20.3952 4.9765 20.5601 4.98566C20.7254 4.97535 20.8887 5.02718 21.0187 5.13121C21.119 5.21595 21.1969 5.32481 21.245 5.44785C21.2868 5.56214 21.3433 5.75077 21.4143 6.01377L22.2683 9.20523L23.0239 5.79454C23.0515 5.65336 23.0863 5.51374 23.1282 5.37625C23.1621 5.27337 23.2202 5.18038 23.2975 5.10533C23.3448 5.06301 23.4 5.03064 23.4597 5.0101C23.5195 4.98955 23.5827 4.98125 23.6457 4.98566C23.7171 4.98409 23.7882 4.99683 23.8547 5.02314C23.9213 5.04946 23.9821 5.08884 24.0336 5.13901C24.0854 5.18812 24.1264 5.24778 24.1539 5.31406C24.1813 5.38034 24.1947 5.45176 24.1929 5.52361C24.1825 5.70022 24.1535 5.87521 24.1066 6.04563L23.1463 9.978C23.0806 10.2439 23.0262 10.4386 22.983 10.562C22.9381 10.6875 22.8622 10.7994 22.7625 10.8868C22.6383 10.9866 22.4819 11.0362 22.3235 11.026C22.1692 11.0339 22.0172 10.9859 21.8946 10.8907C21.7946 10.8108 21.7172 10.7056 21.6703 10.5857C21.6282 10.4731 21.5692 10.2799 21.4931 10.0061Z"
                                  fill="#165FE3"
                                />
                              </svg>
                              <span>Products added</span>
                            </h2>
                            <div className="border-t border-blue-700 mb-4"></div>
                            {formValues?.newlyAddedProducts?.map(
                              (item: any) => (
                                <div
                                  key={item.id}
                                  className="bg-[#FEFEFE] rounded-lg border border-gray-200 p-6 mb-4"
                                >
                                  <div className="flex justify-between items-center">
                                    <h3 className="text-black-label text-sm font-medium">
                                      {item?.name}
                                    </h3>
                                    <button
                                      onClick={() => {
                                        setTablePreviewMode({
                                          isOpen: true,
                                          data: forexResponse?.find(
                                            (forexItem: any) =>
                                              forexItem.id === item.id,
                                          ),
                                        });
                                      }}
                                      className="bg-blue-50 text-blue-600 rounded-lg p-3 w-64 flex justify-between items-center"
                                    >
                                      <p>Review currencies or margin</p>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-blue-600"
                                      >
                                        <path d="M7 17l9.2-9.2M17 17V7H7" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}

                        {/* Products Modified Section */}
                        {formValues?.modifiedProducts?.length > 0 && (
                          <div className="">
                            <h2 className="text-blue-700 text-lg font-semibold mb-2 leading-[100%] flex items-center gap-3">
                              Products Modified
                            </h2>
                            <div className="border-t border-blue-700 mb-4"></div>
                            <div className="flex flex-col gap-4">
                              {formValues?.modifiedProducts?.map(
                                (item: any) => {
                                  if ([21, 22].includes(Number(item?.id)))
                                    return (
                                      <div
                                        key={item.id}
                                        className="bg-[#FEFEFE] rounded-lg border border-gray-200 p-6 mb-4"
                                      >
                                        <div className="flex justify-between items-center">
                                          <h3 className="text-black-label text-sm font-medium">
                                            {item?.name}
                                          </h3>
                                          <button
                                            onClick={() => {
                                              setTablePreviewMode({
                                                isOpen: true,
                                                data: forexResponse?.find(
                                                  (forexItem: any) =>
                                                    forexItem.id === item.id,
                                                ),
                                              });
                                            }}
                                            className="bg-blue-50 text-blue-600 rounded-lg p-3 w-64 flex justify-between items-center"
                                          >
                                            <p className="font-medium text-sm leading-[14px]">
                                              Review currencies or margin
                                            </p>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="text-blue-600"
                                            >
                                              <path d="M7 17l9.2-9.2M17 17V7H7" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  return (
                                    <div
                                      key={item.id}
                                      className="bg-white rounded-lg border border-gray-200 p-4"
                                    >
                                      <div className="flex justify-between items-center">
                                        <h3 className="text-black-label text-sm font-medium">
                                          {item?.name}
                                        </h3>
                                        <div className="flex flex-col items-end w-3/5">
                                          <div className="bg-gray-100 rounded-lg p-3 mb-2 w-full justify-between items-center flex ">
                                            <div className="flex items-center gap-2">
                                              <span className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                                                INR
                                              </span>
                                              <span className="text-black-label font-medium text-sm leading-[14px]">
                                                {item?.new_margin}
                                              </span>
                                            </div>
                                            <span className="text-[#B5B5B5] font-medium text-sm leading-[14px]">
                                              Proposed
                                            </span>
                                          </div>
                                          <div className="flex justify-between w-full px-2">
                                            <span className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                                              INR {item?.margin}
                                            </span>
                                            <span className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                                              Previous value
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Footer (optional) */}
          <AlertDialogFooter className="flex justify-start sm:justify-start h-min items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
            {tablePreviewMode?.isOpen ? (
              <button
                onClick={() => {
                  setTablePreviewMode({
                    isOpen: false,
                    data: null,
                  });
                }}
                className="m-0 h-full rounded-lg text-sm font-semibold border-blue-700 border py-[11px] px-4 text-blue-700"
              >
                Back
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setDialogOpen(false);
                    setReviewMode(false);
                    reset();
                  }}
                  className="m-0 h-full rounded-lg text-sm font-semibold border-blue-700 border py-[11px] px-4 text-blue-700"
                >
                  Close
                </button>
                {reviewMode && (
                  <button
                    onClick={() => {
                      setReviewMode(false);
                    }}
                    className="m-0 h-full rounded-lg text-sm font-semibold border-blue-700 border py-[11px] px-4 text-blue-700"
                  >
                    Previous
                  </button>
                )}
                {!reviewMode && hasModifications ? (
                  <button
                    onClick={() => {
                      setReviewMode(!reviewMode);
                      handleDataOverview();
                    }}
                    disabled={!hasModifications}
                    className="m-0 h-full disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-semibold border-blue-700 border py-[11px] px-4 text-blue-700"
                  >
                    Review
                  </button>
                ) : (
                  !(
                    !formValues?.newlyAddedProducts?.length &&
                    !formValues?.modifiedProducts?.length
                  ) && (
                    <button
                      onClick={() => {
                        handleSubmit(onSubmit)();
                        // setDialogOpen(false);
                      }}
                      className="m-0 h-full rounded-lg text-sm font-semibold border-blue-700 border py-[11px] px-4 text-white-A700 bg-blue-700"
                    >
                      Finish
                    </button>
                  )
                )}
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

export default Product;

const checkBuySellMarginSame = (commission_charges: any[]): boolean => {
  const buyMargins = commission_charges?.filter(
    (charge: any) => charge.ProductSubtype === "Buy",
  );
  const sellMargins = commission_charges?.filter(
    (charge: any) => charge.ProductSubtype === "Sell",
  );

  const allBuyMarginsSame = buyMargins.every(
    (charge: any) => charge.charges === buyMargins[0]?.charges,
  );
  const allSellMarginsSame = sellMargins.every(
    (charge: any) => charge.charges === sellMargins[0]?.charges,
  );

  return allBuyMarginsSame && allSellMarginsSame;
};

const CurrencyMarginTable = ({
  item,
  handleCommissionTypeChange,
  handleCurrencyMargin,
  forexResponse,
  showModified = false,
}: any) => {
  let commission_charges = item?.commission_charges || [];
  if (showModified) {
    commission_charges = commission_charges?.filter(
      (charge: any) => charge.charges !== charge.original_charges,
    );
  }
  return (
    <div className="w-full space-y-4">
      <table className="w-full">
        <thead className="bg-black-900 w-full text-white-A700 rounded-t-md block">
          <tr className="flex">
            <th
              scope="col"
              className="py-2 flex-1 text-left text-sm font-medium text-white-A700 px-2"
            >
              Country
            </th>
            <th
              scope="col"
              className="py-2 flex-1 text-left text-sm font-medium text-white-A700 px-2"
            >
              Margin
            </th>
            {/* <th scope='col' className="py-2 flex-1 text-left text-sm font-medium text-white-A700 px-2">Sell Margin</th> */}
          </tr>
        </thead>
        <tbody>
          {commission_charges.map((countryMargin: any) => (
            <tr
              key={countryMargin.country}
              className="flex border-b last:border-0"
            >
              <td className="py-3 flex-1 text-gray-700 px-2 flex items-center">
                {countryMargin.Country_Currency_ISO}
              </td>
              <td className="py-3 flex-1 px-2">
                <span className="text-xs text-gray-500">
                  {`${countryMargin.ProductSubtype}`} Margin
                </span>
                <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden  p-1 h-10">
                  <Select
                    value={countryMargin.CommisionType}
                    onValueChange={(val) => {
                      handleCommissionTypeChange(
                        item.id,
                        countryMargin?.id,
                        val,
                      );
                      console.log(forexResponse);
                    }}
                  >
                    <SelectTrigger
                      disabled
                      className="w-[70px] h-8 text-sm border-none text-blue-700 bg-[#E1EBFC] rounded-md focus:ring-0"
                    >
                      <SelectValue placeholder="%" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem className="text-blue-700" value="commision">
                        %
                      </SelectItem>
                      <SelectItem className="text-blue-700" value="flat">
                        Flat
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={countryMargin.charges}
                    disabled={showModified}
                    onChange={(e) =>
                      handleCurrencyMargin(
                        item.id,
                        countryMargin?.id,
                        e.target.value,
                      )
                    }
                    className="w-full h-9 text-sm border-none rounded-none disabled:text-gray-400"
                  />
                  {countryMargin?.isModified ? (
                    <span className="text-xs text-gray-400 pr-2">Proposed</span>
                  ) : (
                    <span className="text-xs text-gray-400 pr-2">Default</span>
                  )}
                </div>
                {countryMargin?.isModified && (
                  <div className="flex items-center px-1 justify-between mt-1">
                    <small className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                      INR {countryMargin?.original_charges}
                    </small>
                    <small className="text-[#B5B5B5] font-normal text-xs leading-[140%] ">
                      Previous value
                    </small>
                  </div>
                )}
              </td>
              {/* <td className="py-3 flex-1 px-2">
              <span className="text-xs text-gray-500">Sell Margin</span>
              <div className="flex items-center border rounded-md overflow-hidden bg-gray-100 h-9 mt-1">
                <Select
                  defaultValue="%"
                  disabled={!item.isActive}
                >
                  <SelectTrigger className="w-[60px] h-9 text-sm border-none bg-gray-100 rounded-none">
                    <SelectValue placeholder="%" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="%">%</SelectItem>
                    <SelectItem value="Flat">Flat</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={countryMargin.sell_margin}
                  onChange={(e) => handleCountryMarginChange(
                    item.id,
                    countryMargin.country,
                    'sell_margin',
                    e.target.value
                  )}
                  autoFocus
                  readOnly={!item.isActive}
                  disabled={!item.isActive}
                  className="w-full h-9 text-sm border-none bg-gray-100 rounded-none disabled:text-gray-400"
                />
              </div>
            </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const isForexModified = (forexProduct: any): boolean => {
  return forexProduct.commission_charges?.some((charge: any) => {
    return Number(charge.charges) !== Number(charge.original_charges);
  });
};
